/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  Component,
  ContentChild,
  DoCheck,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef
} from '@angular/core';

import type {AdnGridsterItem, AdnGridsterLegacyOptions} from '../../config/gridster-config';
import {
  buildGridster2Options,
  compactGridItemsUp,
  computeGridsterMinRows,
  createGridsterItem,
  hasSavedGridPosition,
  normalizeGridsterItemBounds,
  syncLegacyGridsterFields
} from '../../config/gridster-config';
import template from './gridster-workspace.component.html';

@Component({
  standalone: false,
  selector: 'adn-gridster-workspace',
  template
})
export class GridsterWorkspaceComponent implements OnChanges, DoCheck, OnDestroy {
  @Input() items: any = [];
  @Input() itemScopeKey = 'item';
  @Input() trackByField = 'id';
  @Input() workspaceClasses: string | string[] | Record<string, boolean> = 'gridsterWorkspace';
  @Input() options: AdnGridsterLegacyOptions = {};
  @Input() scopeExtras: Record<string, any> = {};
  @Input() layoutChange: () => void;

  @ContentChild('gridsterItemTemplate') itemTemplateRef: TemplateRef<any>;

  gridItems: AdnGridsterItem[] = [];
  gridsterOptions = buildGridster2Options({});

  private itemsSignature = '';
  private lastScopeExtras: Record<string, any>;
  private readonly itemScopeCache = new WeakMap<any, Record<string, any>>();
  private layoutRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private gridsterApi: {getFirstPossiblePosition?: (item: any) => any} | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options || changes.layoutChange) {
      // Parents may bind [options]="gridsterOpts" before opts are initialized.
      // Angular overwrites the field default with undefined in that case.
      this.gridsterApi = null;
      this.gridsterOptions = buildGridster2Options(this.getOptions(), () => {
        // User drag/resize finished — compact gaps, then notify parent dirty.
        this.scheduleLayoutRefresh(true);
        this.notifyLayoutChange();
      });
      const optionsRef = this.gridsterOptions as {
        initCallback?: (gridster: unknown, api: {getFirstPossiblePosition?: (item: any) => any}) => void;
      };
      const previousInit = optionsRef.initCallback;
      optionsRef.initCallback = (gridster, api) => {
        this.gridsterApi = api;
        previousInit?.(gridster, api);
      };
    }
    if (changes.items || changes.options) {
      this.syncGridItems(true);
    }
    if (changes.scopeExtras || changes.itemScopeKey) {
      this.lastScopeExtras = undefined;
    }
  }

  ngDoCheck(): void {
    const signature = this.buildItemsSignature();
    if (signature !== this.itemsSignature) {
      this.syncGridItems(false);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.layoutRefreshTimer !== null) {
      clearTimeout(this.layoutRefreshTimer);
      this.layoutRefreshTimer = null;
    }
  }

  trackByGridItem(_index: number, gridItem: AdnGridsterItem): any {
    const trackId = gridItem.data?.[this.trackByField];
    if (trackId !== undefined && trackId !== null) {
      return trackId;
    }
    const data = gridItem.data;
    return `${data?.reportComponent?.id ?? data?.id ?? 'item'}-${_index}`;
  }

  getItemScope(gridItem: AdnGridsterItem): Record<string, any> {
    const data = gridItem.data;
    if (!data) {
      return this.scopeExtras;
    }
    const extrasChanged = this.lastScopeExtras !== this.scopeExtras;
    let scope = this.itemScopeCache.get(data);
    if (!scope || extrasChanged || scope.$implicit !== data || scope[this.itemScopeKey] !== data) {
      scope = {
        ...this.scopeExtras,
        $implicit: data,
        [this.itemScopeKey]: data
      };
      this.itemScopeCache.set(data, scope);
      this.lastScopeExtras = this.scopeExtras;
    }
    return scope;
  }

  private notifyLayoutChange(): void {
    if (typeof this.layoutChange === 'function') {
      this.layoutChange();
    }
  }

  private getItemsArray(): any[] {
    const items = this.items;
    if (Array.isArray(items)) {
      return items;
    }
    if (items && Array.isArray(items.components)) {
      return items.components;
    }
    return [];
  }

  /**
   * Sync data items → gridster item configs.
   * angular-gridster2 v21 uses signal inputs + effects that markForCheck inside
   * calculateLayout. Mutating geometry in place does not update those inputs, and
   * calling api.calculateLayout() from DoCheck/sync re-enters zoneless CD (NG0103).
   * Push new item object identities when geometry changes; leave layout to gridster.
   */
  private syncGridItems(_fromInputChange: boolean): void {
    const items = this.getItemsArray();
    const maxCols = this.gridsterOptions.maxCols ?? this.gridsterOptions.minCols ?? 6;
    const existingById = new Map<any, AdnGridsterItem>();
    const existingByData = new Map<any, AdnGridsterItem>();
    this.gridItems.forEach((gridItem) => {
      const id = gridItem.data?.[this.trackByField];
      if (id !== undefined && id !== null) {
        existingById.set(id, gridItem);
      }
      if (gridItem.data) {
        existingByData.set(gridItem.data, gridItem);
      }
    });

    let needsAutoPosition = false;
    const nextGridItems = items.map((dataItem) => {
      const id = dataItem?.[this.trackByField];
      const existing = (id !== undefined && id !== null ? existingById.get(id) : undefined)
        ?? existingByData.get(dataItem);
      if (existing) {
        const dataReplaced = existing.data !== dataItem;
        const nextCols = dataItem.sizeX ?? existing.cols;
        const nextRows = Math.max(dataItem.sizeY ?? existing.rows ?? 1, 1);
        let nextX = existing.x;
        let nextY = existing.y;
        if (hasSavedGridPosition(dataItem) && (dataReplaced || existing.x < 0 || existing.y < 0)) {
          nextX = dataItem.col ?? dataItem.x ?? existing.x ?? 0;
          nextY = dataItem.row ?? dataItem.y ?? existing.y ?? 0;
        }
        if (!hasSavedGridPosition(dataItem) && (nextX < 0 || nextY < 0)) {
          needsAutoPosition = true;
        }
        const geometryChanged = existing.cols !== nextCols
          || existing.rows !== nextRows
          || existing.x !== nextX
          || existing.y !== nextY
          || dataReplaced;
        if (!geometryChanged) {
          return existing;
        }
        // New identity so gridster-item's signal input() notices geometry updates.
        const updated: AdnGridsterItem = {
          ...existing,
          data: dataItem,
          cols: nextCols,
          rows: nextRows,
          x: nextX,
          y: nextY
        };
        normalizeGridsterItemBounds(updated, maxCols);
        syncLegacyGridsterFields(updated);
        return updated;
      }
      const created = createGridsterItem(dataItem, this.getOptions(), maxCols);
      if (!hasSavedGridPosition(dataItem)) {
        needsAutoPosition = true;
      }
      return created;
    });

    const sameRefs = nextGridItems.length === this.gridItems.length
      && nextGridItems.every((gridItem, index) => gridItem === this.gridItems[index]);
    if (!sameRefs) {
      this.gridItems = nextGridItems;
    }

    // Identity/size only — never col/row. Gridster + itemChangeCallback own those;
    // watching them re-enters sync → layout → NG0103.
    this.itemsSignature = this.buildItemsSignature();

    // Auto-position only when tiles lack coords. Do not call calculateLayout on
    // every input change — gridster's init/effects already lay out, and an extra
    // undebounced calculateLayout markForCheck storm was NG0103 on workspace edit.
    if (needsAutoPosition) {
      this.scheduleLayoutRefresh(false);
    } else {
      this.updateGridsterMinRows();
    }
  }

  private getOptions(): AdnGridsterLegacyOptions {
    return this.options || {};
  }

  private scheduleLayoutRefresh(compact: boolean): void {
    if (this.layoutRefreshTimer !== null) {
      clearTimeout(this.layoutRefreshTimer);
    }
    this.layoutRefreshTimer = setTimeout(() => {
      this.layoutRefreshTimer = null;
      if (this.destroyed) {
        return;
      }
      this.autoPositionGridItems();
      if (compact) {
        compactGridItemsUp(this.gridItems);
      }
      this.updateGridsterMinRows();
      // New identities (not calculateLayout): gridster v21 signal inputs + debounced
      // calculateLayout$ pick this up. Direct api.calculateLayout() markForCheck
      // during zoneless synchronize was NG0103.
      this.gridItems = this.gridItems.map((item) => ({...item}));
      this.itemsSignature = this.buildItemsSignature();
    }, 0);
  }

  private updateGridsterMinRows(): void {
    const options = this.getOptions();
    const fallbackMinRows = this.gridItems.length
      ? (options.minRows ?? 1)
      : (options.emptyMinRows ?? options.minRows ?? 1);
    const nextMinRows = computeGridsterMinRows(this.gridItems, fallbackMinRows);
    if (this.gridsterOptions.minRows !== nextMinRows) {
      // In-place only — replacing options identity re-fires gridster's options
      // effect → calculateLayout → markForCheck (NG0103 under zoneless CD).
      // Item bottoms already drive grid height in setGridDimensions.
      this.gridsterOptions.minRows = nextMinRows;
    }
  }

  private autoPositionGridItems(): void {
    const getFirstPossiblePosition = this.gridsterApi?.getFirstPossiblePosition
      ?? (this.gridsterOptions as {api?: {getFirstPossiblePosition?: (item: any) => any}})?.api?.getFirstPossiblePosition;
    if (typeof getFirstPossiblePosition !== 'function') {
      // gridster addItem already auto-positions x/y === -1 when api is ready.
      return;
    }

    for (const gridItem of this.gridItems) {
      if (hasSavedGridPosition(gridItem.data)) {
        continue;
      }
      const positioned = getFirstPossiblePosition({
        cols: gridItem.cols,
        rows: gridItem.rows,
        x: gridItem.x >= 0 ? gridItem.x : 0,
        y: gridItem.y >= 0 ? gridItem.y : 0
      });
      if (gridItem.x !== positioned.x || gridItem.y !== positioned.y) {
        gridItem.x = positioned.x;
        gridItem.y = positioned.y;
        syncLegacyGridsterFields(gridItem);
      }
    }
  }

  /**
   * Membership + size only. Intentionally omits col/row — those are written by
   * gridster itemChangeCallback / compact and must not retrigger sync.
   */
  private buildItemsSignature(): string {
    return this.getItemsArray().map((item) => [
      item?.[this.trackByField],
      item?.uniqueId,
      item?.reportComponent?.id,
      item?.sizeX,
      item?.sizeY
    ].join(':')).join('|');
  }
}
